import {
    CalendarDate,
    CommunityId,
    DefaultReferences,
    EMPTY_REFERRAL_SENTINEL,
    PromptId,
} from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import { useQuery } from "@tanstack/react-query";
import type { inferRouterOutputs } from "@trpc/server";
import { CalendarDateInput } from "components/ui/calendar-date";
import { defaultCardStyle } from "components/ui/card";
import { Select } from "components/ui/select";
import { Spinner } from "components/ui/spinner";
import { useCommunityNamesQuery, useGetUsersForRegionQuery } from "generated/graphql";
import React from "react";
import Multiselect from "react-widgets/Multiselect";
import { useCommunityTypeQuestion, useRegionId, useRegions } from "store/selectors/hooks";
import { stableQueryOptions } from "util/requests";
import { ContentContainer, ContentFull, PageHeader, Stage } from "../../layout";
import { trpc } from "../../rpc/trpc";

type RouterOutput = inferRouterOutputs<AppRouter>;
type StageConversionData = RouterOutput["stageConversion"]["stageConversion"];
type StageRow = StageConversionData["stages"][number];
type AllUsersData = RouterOutput["stageConversion"]["stageConversionAllUsers"];
type UserBreakdownRow = AllUsersData["userBreakdowns"][number];

function escapeCsvValue(value: string | number): string {
    const str = String(value);
    if (str.includes(",") || str.includes('"') || str.includes("\n")) {
        return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
}

function triggerCsvDownload(rows: (string | number)[][], filename: string): void {
    const csvContent = rows.map(row => row.map(escapeCsvValue).join(",")).join("\n");
    const blob = new Blob([csvContent], {
        type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
}

function buildCsvFilename(startDate: string, endDate: string): string {
    return `stage-conversion-${startDate}-to-${endDate}.csv`;
}

function generateSingleUserCsv(
    stages: StageRow[],
    teamStages: StageRow[] | undefined,
    advisorName: string | undefined,
): (string | number)[][] {
    if (teamStages && advisorName) {
        const teamMap = new Map<string, StageRow>();
        for (const s of teamStages) {
            teamMap.set(s.stageId, s);
        }
        const header = [
            "Stage",
            `Reach Rate (${advisorName})`,
            `Close Rate (${advisorName})`,
            "Team Reach Rate",
            "Team Close Rate",
            "Reach Rate Variance (pp)",
            "Close Rate Variance (pp)",
        ];
        const dataRows: (string | number)[][] = [header];
        for (const stage of stages) {
            const teamRow = teamMap.get(stage.stageId);
            dataRows.push([
                stage.stageName,
                stage.reachRate,
                stage.closeRate,
                teamRow?.reachRate ?? "",
                teamRow?.closeRate ?? "",
                teamRow ? stage.reachRate - teamRow.reachRate : "",
                teamRow ? stage.closeRate - teamRow.closeRate : "",
            ]);
        }
        return dataRows;
    }
    const header = ["Stage", "Reach Rate", "Close Rate", "Clients"];
    const dataRows: (string | number)[][] = [header];
    for (const stage of stages) {
        dataRows.push([
            stage.stageName,
            stage.reachRate,
            stage.closeRate,
            stage.clientCount,
        ]);
    }
    return dataRows;
}

function generateMatrixCsv(
    teamResult: StageConversionData,
    userBreakdowns: UserBreakdownRow[],
    userNameMap: Map<string, string>,
): (string | number)[][] {
    const sortedBreakdowns = [...userBreakdowns].sort((a, b) => {
        const nameA = userNameMap.get(a.userId) ?? "";
        const nameB = userNameMap.get(b.userId) ?? "";
        return nameA.toLocaleLowerCase().localeCompare(nameB.toLocaleLowerCase());
    });

    const header: (string | number)[] = [
        "Stage",
        "Team Avg Reach Rate",
        "Team Avg Close Rate",
    ];
    for (const ub of sortedBreakdowns) {
        const name = userNameMap.get(ub.userId) ?? "Unknown";
        header.push(`${name} Reach Rate`, `${name} Close Rate`);
    }

    const userStageMaps = new Map<
        string,
        Map<string, { reachRate: number; closeRate: number }>
    >();
    for (const ub of sortedBreakdowns) {
        const stageMap = new Map<string, { reachRate: number; closeRate: number }>();
        for (const s of ub.stages) {
            stageMap.set(s.stageId, {
                reachRate: s.reachRate,
                closeRate: s.closeRate,
            });
        }
        userStageMaps.set(ub.userId, stageMap);
    }

    const dataRows: (string | number)[][] = [header];
    for (const stage of teamResult.stages) {
        const row: (string | number)[] = [
            stage.stageName,
            stage.reachRate,
            stage.closeRate,
        ];
        for (const ub of sortedBreakdowns) {
            const rates = userStageMaps.get(ub.userId)?.get(stage.stageId);
            row.push(rates?.reachRate ?? 0, rates?.closeRate ?? 0);
        }
        dataRows.push(row);
    }
    return dataRows;
}

const DownloadCsvButton: React.FC<{ onClick: () => void }> = ({ onClick }) => {
    return (
        <button
            onClick={onClick}
            className="px-3 py-1.5 text-sm font-medium rounded bg-gray-100 text-gray-700 hover:bg-gray-200">
            Download CSV
        </button>
    );
};

const DATE_PRESETS: { label: string; getDates: () => [CalendarDate, CalendarDate] }[] = [
    {
        label: "30d",
        getDates: () => [CalendarDate.today.addDays(-30), CalendarDate.today],
    },
    {
        label: "90d",
        getDates: () => [CalendarDate.today.addDays(-90), CalendarDate.today],
    },
    {
        label: "12mo",
        getDates: () => [CalendarDate.today.addDays(-365), CalendarDate.today],
    },
    {
        label: "YTD",
        getDates: () => [
            new CalendarDate(CalendarDate.today.year, 1, 1),
            CalendarDate.today,
        ],
    },
    {
        label: "All Time",
        getDates: () => [new CalendarDate(2000, 1, 1), CalendarDate.today],
    },
];

export const StageConversion: React.FC = () => {
    const [startDate, setStartDate] = React.useState(() =>
        CalendarDate.today.addDays(-365),
    );
    const [endDate, setEndDate] = React.useState(() => CalendarDate.today);
    const [selectedUserId, setSelectedUserId] = React.useState("");
    const [compareAll, setCompareAll] = React.useState(false);
    const [filtersExpanded, setFiltersExpanded] = React.useState(false);
    const [selectedReferralSources, setSelectedReferralSources] = React.useState<
        string[]
    >([]);
    const [selectedCareTypes, setSelectedCareTypes] = React.useState<PromptId[]>([]);
    const regionId = useRegionId();
    const validQuery = regionId && startDate && endDate;

    const users = useGetUsersForRegionQuery(
        { regionId },
        { ...stableQueryOptions(), enabled: !!regionId },
    );

    const activeUsers = React.useMemo(() => {
        if (!users.data) return [];
        return users.data.getUsersForRegion
            .filter(u => !u.hidden && !u.disabled)
            .sort((a, b) =>
                a.preferredName
                    .toLocaleLowerCase()
                    .localeCompare(b.preferredName.toLocaleLowerCase()),
            );
    }, [users.data]);

    const selectedAdvisorName = React.useMemo(() => {
        if (!selectedUserId) return undefined;
        const user = activeUsers.find(u => u.id === selectedUserId);
        return user?.preferredName;
    }, [selectedUserId, activeUsers]);

    const referralSourceOptions = React.useMemo(
        () =>
            DefaultReferences.filter(r => r.key !== EMPTY_REFERRAL_SENTINEL).map(r => ({
                key: r.key,
                text: r.text,
            })),
        [],
    );

    const regions = useRegions();
    const region = regions.find(r => r.id === regionId);
    const communityTypeQuestion = useCommunityTypeQuestion(region?.key, regionId);

    const careTypeOptions = React.useMemo(() => {
        if (!communityTypeQuestion?.prompts) return [];
        return communityTypeQuestion.prompts
            .filter(p => !p.deleted)
            .map(p => ({ key: p.id as PromptId, text: p.title || "" }));
    }, [communityTypeQuestion]);

    const [selectedCommunities, setSelectedCommunities] = React.useState<CommunityId[]>(
        [],
    );
    const communityNamesData = useCommunityNamesQuery(
        { regionKey: region?.key ?? "" },
        { ...stableQueryOptions(), enabled: !!region?.key },
    );

    const communityOptions = React.useMemo(() => {
        if (!communityNamesData.data?.communityNames?.communities) return [];
        return communityNamesData.data.communityNames.communities
            .filter(c => c.name)
            .map(c => ({ key: c.id as CommunityId, text: c.name }))
            .sort((a, b) => a.text.localeCompare(b.text));
    }, [communityNamesData.data]);

    const applyPreset = (preset: (typeof DATE_PRESETS)[number]) => {
        const [s, e] = preset.getDates();
        setStartDate(s);
        setEndDate(e);
    };

    return (
        <Stage>
            <PageHeader title="Stage Conversion">
                <div className="max-w-lg lg:max-w-none lg:flex justify-between lg:space-x-3 items-end">
                    <CalendarDateInput
                        label="Start"
                        value={startDate}
                        onChange={setStartDate}
                    />
                    <CalendarDateInput
                        label="End"
                        value={endDate}
                        onChange={setEndDate}
                    />
                    <div className="flex space-x-1 mt-2 lg:mt-0">
                        {DATE_PRESETS.map(preset => (
                            <button
                                key={preset.label}
                                onClick={() => applyPreset(preset)}
                                className="px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded hover:bg-gray-200">
                                {preset.label}
                            </button>
                        ))}
                    </div>
                </div>
            </PageHeader>
            <div className="px-4 py-3 border-b border-gray-200">
                <div className="flex flex-wrap items-end gap-3">
                    <Select
                        label="Advisor"
                        value={compareAll ? "" : selectedUserId}
                        onChange={e => {
                            setSelectedUserId(e.target.value);
                            setCompareAll(false);
                        }}>
                        <option value="">Team Average</option>
                        {activeUsers.map(u => (
                            <option key={u.id} value={u.id}>
                                {u.preferredName}
                            </option>
                        ))}
                    </Select>
                    <div className="flex items-end">
                        <button
                            onClick={() => {
                                setCompareAll(!compareAll);
                                if (!compareAll) setSelectedUserId("");
                            }}
                            className={`px-3 py-1.5 text-sm font-medium rounded ${
                                compareAll
                                    ? "bg-blue-600 text-white"
                                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                            }`}>
                            Compare All
                        </button>
                    </div>
                    <button
                        onClick={() => setFiltersExpanded(!filtersExpanded)}
                        className="md:hidden px-3 py-1.5 text-sm font-medium rounded bg-gray-100 text-gray-700 hover:bg-gray-200">
                        {filtersExpanded ? "Hide Filters" : "More Filters"}
                    </button>
                </div>
                <div
                    className={`${filtersExpanded ? "" : "hidden"} md:flex md:flex-wrap items-end gap-3 mt-3 space-y-2 md:space-y-0`}>
                    <div>
                        <div className="mb-1 text-sm">Referral Source</div>
                        <Multiselect
                            data={referralSourceOptions}
                            value={selectedReferralSources}
                            textField="text"
                            dataKey="key"
                            placeholder="All sources"
                            onChange={values =>
                                setSelectedReferralSources(values.map(v => v.key))
                            }
                        />
                    </div>
                    {careTypeOptions.length > 0 ? (
                        <div>
                            <div className="mb-1 text-sm">Care Type</div>
                            <Multiselect
                                data={careTypeOptions}
                                value={selectedCareTypes}
                                textField="text"
                                dataKey="key"
                                placeholder="All types"
                                onChange={values =>
                                    setSelectedCareTypes(values.map(v => v.key))
                                }
                            />
                        </div>
                    ) : null}
                    {communityOptions.length > 0 ? (
                        <div>
                            <div className="mb-1 text-sm">Community</div>
                            <Multiselect
                                data={communityOptions}
                                value={selectedCommunities}
                                textField="text"
                                dataKey="key"
                                placeholder="All communities"
                                onChange={values =>
                                    setSelectedCommunities(values.map(v => v.key))
                                }
                            />
                        </div>
                    ) : null}
                </div>
            </div>
            <ContentContainer>
                {validQuery && compareAll ? (
                    <AllUsersContent
                        startDate={startDate.toString()}
                        endDate={endDate.toString()}
                        regionId={regionId}
                        referralSourceIds={
                            selectedReferralSources.length > 0
                                ? selectedReferralSources
                                : undefined
                        }
                        careTypeIds={
                            selectedCareTypes.length > 0 ? selectedCareTypes : undefined
                        }
                        communityIds={
                            selectedCommunities.length > 0
                                ? selectedCommunities
                                : undefined
                        }
                        activeUsers={activeUsers}
                        onSelectAdvisor={userId => {
                            setSelectedUserId(userId);
                            setCompareAll(false);
                        }}
                    />
                ) : validQuery ? (
                    <StageConversionContent
                        startDate={startDate.toString()}
                        endDate={endDate.toString()}
                        regionId={regionId}
                        userId={selectedUserId || undefined}
                        advisorName={selectedAdvisorName}
                        referralSourceIds={
                            selectedReferralSources.length > 0
                                ? selectedReferralSources
                                : undefined
                        }
                        careTypeIds={
                            selectedCareTypes.length > 0 ? selectedCareTypes : undefined
                        }
                        communityIds={
                            selectedCommunities.length > 0
                                ? selectedCommunities
                                : undefined
                        }
                    />
                ) : null}
            </ContentContainer>
        </Stage>
    );
};

const StageConversionContent: React.FC<{
    startDate: string;
    endDate: string;
    regionId: string;
    userId?: string;
    advisorName?: string;
    referralSourceIds?: string[];
    careTypeIds?: string[];
    communityIds?: string[];
}> = ({
    startDate,
    endDate,
    regionId,
    userId,
    advisorName,
    referralSourceIds,
    careTypeIds,
    communityIds,
}) => {
    const trpcClient = trpc.useTRPC();
    const baseParams = {
        startDate,
        endDate,
        regionId,
        referralSourceIds,
        careTypeIds,
        communityIds,
    };

    const advisorQuery = useQuery({
        ...trpcClient.stageConversion.stageConversion.queryOptions({
            ...baseParams,
            userId: userId!,
        }),
        enabled: !!userId,
    });

    const teamQuery = useQuery(
        trpcClient.stageConversion.stageConversion.queryOptions(baseParams),
    );

    const isLoading = teamQuery.isLoading || (!!userId && advisorQuery.isLoading);
    const error = teamQuery.error || advisorQuery.error;
    const teamData = teamQuery.data;
    const advisorData = userId ? advisorQuery.data : undefined;
    const primaryData = advisorData ?? teamData;

    const advisorLowSample =
        !!advisorData &&
        advisorData.totalActiveClients > 0 &&
        advisorData.totalActiveClients < 10;

    const userInsight = React.useMemo(() => {
        if (!advisorData || !teamData || !advisorName) return undefined;
        if (advisorData.totalActiveClients < 10) return undefined;

        const teamMap = new Map<string, StageRow>();
        for (const s of teamData.stages) {
            teamMap.set(s.stageId, s);
        }

        const diffs: {
            stageName: string;
            advisorRate: number;
            teamRate: number;
            diff: number;
        }[] = [];
        for (const stage of advisorData.stages) {
            const teamStage = teamMap.get(stage.stageId);
            if (!teamStage) continue;
            diffs.push({
                stageName: stage.stageName,
                advisorRate: stage.reachRate,
                teamRate: teamStage.reachRate,
                diff: stage.reachRate - teamStage.reachRate,
            });
        }

        if (diffs.length === 0) return undefined;

        const hasNegativeGap = diffs.some(d => d.diff < 0);

        if (hasNegativeGap) {
            const worst = diffs.reduce((min, d) => (d.diff < min.diff ? d : min));
            const pp = Math.abs(Math.round(worst.diff));
            return `${advisorName}\u2019s biggest gap vs. team average is at ${worst.stageName}: ${worst.advisorRate}% vs. ${worst.teamRate}% (\u2212${pp}pp).`;
        }

        const best = diffs.reduce((max, d) => (d.diff > max.diff ? d : max));
        const pp = Math.round(best.diff);
        return `${advisorName}\u2019s strongest stage vs. team average is ${best.stageName}: ${best.advisorRate}% vs. ${best.teamRate}% (+${pp}pp).`;
    }, [advisorData, teamData, advisorName]);

    const allInsights = React.useMemo(() => {
        const base = primaryData?.insights ?? [];
        if (userInsight) return [...base, userInsight];
        return base;
    }, [primaryData?.insights, userInsight]);

    if (isLoading) {
        return (
            <ContentFull>
                <Spinner />
            </ContentFull>
        );
    }

    if (error) {
        return (
            <ContentFull>
                <p className="text-red-600">
                    {(error as { message?: string })?.message ?? "Failed to load report"}
                </p>
            </ContentFull>
        );
    }

    if (!primaryData || primaryData.totalActiveClients === 0) {
        return (
            <ContentFull>
                <div className="text-center py-12 text-gray-500">
                    <p className="text-lg font-medium">No data available</p>
                    <p className="mt-1 text-sm">
                        No active clients found in the selected date range.
                    </p>
                </div>
            </ContentFull>
        );
    }

    return (
        <div className="space-y-4">
            <SummaryBar totalActiveClients={primaryData.totalActiveClients} />
            <div className="flex justify-end">
                <DownloadCsvButton
                    onClick={() => {
                        triggerCsvDownload(
                            generateSingleUserCsv(
                                primaryData.stages,
                                advisorData ? teamData?.stages : undefined,
                                advisorName,
                            ),
                            buildCsvFilename(startDate, endDate),
                        );
                    }}
                />
            </div>
            {advisorLowSample ? (
                <LowSampleNotice totalClients={advisorData.totalActiveClients} />
            ) : null}
            <ConversionTable
                stages={primaryData.stages}
                teamStages={advisorData ? teamData?.stages : undefined}
                advisorName={advisorName}
                advisorTotalClients={advisorData?.totalActiveClients}
            />
            {allInsights.length > 0 ? <InsightStrip insights={allInsights} /> : null}
        </div>
    );
};

const SummaryBar: React.FC<{ totalActiveClients: number }> = ({ totalActiveClients }) => {
    return (
        <ContentFull>
            <div className="flex items-center space-x-4">
                <div>
                    <span className="text-xs font-medium text-gray-500">
                        Active Clients
                    </span>
                    <span className="ml-2 text-lg font-semibold text-zinc-950">
                        {totalActiveClients}
                    </span>
                </div>
            </div>
        </ContentFull>
    );
};

const LowSampleNotice: React.FC<{ totalClients: number }> = ({ totalClients }) => {
    return (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            Low sample size {"\u2014"} results may not be statistically meaningful (n ={" "}
            {totalClients})
        </div>
    );
};

const TAG_STYLES: Record<string, { bg: string; text: string; label: string }> = {
    highDropoff: {
        bg: "bg-amber-100",
        text: "text-amber-800",
        label: "High Dropoff",
    },
    strongClosePredictor: {
        bg: "bg-emerald-100",
        text: "text-emerald-800",
        label: "Strong Close Predictor",
    },
};

const ConversionTable: React.FC<{
    stages: StageRow[];
    teamStages?: StageRow[];
    advisorName?: string;
    advisorTotalClients?: number;
}> = ({ stages, teamStages, advisorName, advisorTotalClients }) => {
    const maxReach = Math.max(...stages.map(s => s.reachRate), 1);
    const teamStageMap = React.useMemo(() => {
        if (!teamStages) return undefined;
        const map = new Map<string, StageRow>();
        for (const s of teamStages) {
            map.set(s.stageId, s);
        }
        return map;
    }, [teamStages]);

    const showVariance =
        !!teamStageMap && advisorTotalClients !== undefined && advisorTotalClients >= 10;

    const reachLabel = advisorName ? `Reach Rate (${advisorName})` : "Reach Rate";
    const closeLabel = advisorName ? `Close Rate (${advisorName})` : "Close Rate";

    return (
        <div className={`overflow-x-auto ${defaultCardStyle}`}>
            <table className="w-full">
                <thead>
                    <tr>
                        <th className="py-2 px-3 bg-gray-200 text-gray-600 uppercase text-xs text-left md:w-48">
                            Stage
                        </th>
                        <th className="py-2 px-3 bg-gray-200 text-gray-600 uppercase text-xs text-left">
                            {reachLabel}
                        </th>
                        <th className="py-2 px-3 bg-gray-200 text-gray-600 uppercase text-xs text-left">
                            {closeLabel}
                        </th>
                        <th className="py-2 px-3 bg-gray-200 text-gray-600 uppercase text-xs text-right w-24">
                            Clients
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {stages.map((stage, i) => {
                        const teamRow = teamStageMap?.get(stage.stageId);
                        return (
                            <tr
                                key={stage.stageId}
                                className={`border-b border-gray-200 hover:bg-gray-100 ${i % 2 === 0 ? "bg-white" : "bg-gray-50"}`}>
                                <td className="py-2.5 px-3">
                                    <div className="text-sm font-medium text-zinc-950">
                                        {stage.stageName}
                                    </div>
                                    {stage.tags.length > 0 ? (
                                        <div className="flex gap-1 mt-1">
                                            {stage.tags.map(tag => {
                                                const style = TAG_STYLES[tag];
                                                return style ? (
                                                    <span
                                                        key={tag}
                                                        className={`inline-block px-1.5 py-0.5 text-[10px] font-medium rounded ${style.bg} ${style.text}`}>
                                                        {style.label}
                                                    </span>
                                                ) : null;
                                            })}
                                        </div>
                                    ) : null}
                                </td>
                                <td className="py-2.5 px-3">
                                    <DualRateBar
                                        rate={stage.reachRate}
                                        teamRate={teamRow?.reachRate}
                                        maxRate={maxReach}
                                        color="#2A6496"
                                    />
                                    {showVariance && teamRow ? (
                                        <VarianceBadge
                                            advisorRate={stage.reachRate}
                                            teamRate={teamRow.reachRate}
                                        />
                                    ) : null}
                                </td>
                                <td className="py-2.5 px-3">
                                    <DualRateBar
                                        rate={stage.closeRate}
                                        teamRate={teamRow?.closeRate}
                                        maxRate={100}
                                        color="#2D7A4F"
                                    />
                                    {showVariance && teamRow ? (
                                        <VarianceBadge
                                            advisorRate={stage.closeRate}
                                            teamRate={teamRow.closeRate}
                                        />
                                    ) : null}
                                </td>
                                <td className="py-2.5 px-3 text-sm text-right tabular-nums text-zinc-950">
                                    {stage.clientCount}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

const VarianceBadge: React.FC<{
    advisorRate: number;
    teamRate: number;
}> = ({ advisorRate, teamRate }) => {
    const diff = Math.round(advisorRate - teamRate);
    if (Math.abs(diff) < 10) return null;
    const isPositive = diff > 0;
    const bgColor = isPositive ? "bg-emerald-100" : "bg-orange-100";
    const textColor = isPositive ? "text-emerald-800" : "text-orange-800";
    const sign = isPositive ? "+" : "\u2212";

    return (
        <span
            className={`inline-block mt-1 px-1.5 py-0.5 text-[10px] font-medium rounded ${bgColor} ${textColor}`}>
            {sign}
            {Math.abs(diff)}pp vs. team
        </span>
    );
};

const DualRateBar: React.FC<{
    rate: number;
    teamRate?: number;
    maxRate: number;
    color: string;
}> = ({ rate, teamRate, maxRate, color }) => {
    const width = maxRate > 0 ? (rate / maxRate) * 100 : 0;
    const teamWidth =
        teamRate !== undefined && maxRate > 0 ? (teamRate / maxRate) * 100 : undefined;

    return (
        <div className="flex items-center md:space-x-2">
            <div className="relative flex-1 h-4 bg-gray-100 rounded overflow-hidden hidden md:block">
                {teamWidth !== undefined ? (
                    <div
                        className="absolute inset-y-0 left-0 rounded"
                        style={{
                            width: `${teamWidth}%`,
                            border: `1.5px dashed ${color}`,
                            opacity: 0.4,
                            minWidth: teamRate && teamRate > 0 ? 4 : 0,
                        }}
                    />
                ) : null}
                <div
                    className="h-full rounded relative"
                    style={{
                        width: `${width}%`,
                        backgroundColor: color,
                        minWidth: rate > 0 ? 4 : 0,
                    }}
                />
            </div>
            <span className="text-xs font-medium tabular-nums text-zinc-700 w-12 text-right">
                {rate}%
            </span>
        </div>
    );
};

type ActiveUser = { id: string; preferredName: string };

const AllUsersContent: React.FC<{
    startDate: string;
    endDate: string;
    regionId: string;
    referralSourceIds?: string[];
    careTypeIds?: string[];
    communityIds?: string[];
    activeUsers: ActiveUser[];
    onSelectAdvisor: (userId: string) => void;
}> = ({
    startDate,
    endDate,
    regionId,
    referralSourceIds,
    careTypeIds,
    communityIds,
    activeUsers,
    onSelectAdvisor,
}) => {
    const trpcClient = trpc.useTRPC();
    const query = useQuery(
        trpcClient.stageConversion.stageConversionAllUsers.queryOptions({
            startDate,
            endDate,
            regionId,
            referralSourceIds,
            careTypeIds,
            communityIds,
        }),
    );

    if (query.isLoading) {
        return (
            <ContentFull>
                <Spinner />
            </ContentFull>
        );
    }

    if (query.error) {
        return (
            <ContentFull>
                <p className="text-red-600">
                    {(query.error as { message?: string })?.message ??
                        "Failed to load report"}
                </p>
            </ContentFull>
        );
    }

    if (!query.data || query.data.teamResult.totalActiveClients === 0) {
        return (
            <ContentFull>
                <div className="text-center py-12 text-gray-500">
                    <p className="text-lg font-medium">No data available</p>
                    <p className="mt-1 text-sm">
                        No active clients found in the selected date range.
                    </p>
                </div>
            </ContentFull>
        );
    }

    const userNameMap = new Map<string, string>();
    for (const u of activeUsers) {
        userNameMap.set(u.id, u.preferredName);
    }

    return (
        <div className="space-y-4">
            <SummaryBar totalActiveClients={query.data.teamResult.totalActiveClients} />
            <div className="flex justify-end">
                <DownloadCsvButton
                    onClick={() => {
                        triggerCsvDownload(
                            generateMatrixCsv(
                                query.data!.teamResult,
                                query.data!.userBreakdowns,
                                userNameMap,
                            ),
                            buildCsvFilename(startDate, endDate),
                        );
                    }}
                />
            </div>
            <ComparisonMatrix
                teamResult={query.data.teamResult}
                userBreakdowns={query.data.userBreakdowns}
                userNameMap={userNameMap}
                onSelectAdvisor={onSelectAdvisor}
            />
            {query.data.teamResult.insights.length > 0 ? (
                <InsightStrip insights={query.data.teamResult.insights} />
            ) : null}
        </div>
    );
};

const ComparisonMatrix: React.FC<{
    teamResult: StageConversionData;
    userBreakdowns: UserBreakdownRow[];
    userNameMap: Map<string, string>;
    onSelectAdvisor: (userId: string) => void;
}> = ({ teamResult, userBreakdowns, userNameMap, onSelectAdvisor }) => {
    const teamRateByStage = React.useMemo(() => {
        const map = new Map<string, number>();
        for (const s of teamResult.stages) {
            map.set(s.stageId, s.closeRate);
        }
        return map;
    }, [teamResult.stages]);

    const sortedBreakdowns = React.useMemo(
        () =>
            [...userBreakdowns].sort((a, b) => {
                const nameA = userNameMap.get(a.userId) ?? "";
                const nameB = userNameMap.get(b.userId) ?? "";
                return nameA.toLocaleLowerCase().localeCompare(nameB.toLocaleLowerCase());
            }),
        [userBreakdowns, userNameMap],
    );

    const userRateMaps = React.useMemo(() => {
        const maps = new Map<string, Map<string, number>>();
        for (const ub of sortedBreakdowns) {
            const stageMap = new Map<string, number>();
            for (const s of ub.stages) {
                stageMap.set(s.stageId, s.closeRate);
            }
            maps.set(ub.userId, stageMap);
        }
        return maps;
    }, [sortedBreakdowns]);

    return (
        <div className={`overflow-x-auto ${defaultCardStyle}`}>
            <table className="w-full">
                <thead>
                    <tr>
                        <th className="py-2 px-3 bg-gray-200 text-gray-600 uppercase text-xs text-left sticky left-0 z-10 bg-gray-200 w-48">
                            Stage
                        </th>
                        <th className="py-2 px-3 bg-gray-200 text-gray-600 uppercase text-xs text-center min-w-[80px]">
                            Team Avg
                        </th>
                        {sortedBreakdowns.map(ub => (
                            <th
                                key={ub.userId}
                                className="py-2 px-3 bg-gray-200 text-gray-600 uppercase text-xs text-center min-w-[80px] cursor-pointer hover:bg-gray-300"
                                onClick={() => onSelectAdvisor(ub.userId)}>
                                {userNameMap.get(ub.userId) ?? "Unknown"}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {teamResult.stages.map((stage, i) => {
                        const teamRate = teamRateByStage.get(stage.stageId) ?? 0;
                        return (
                            <tr
                                key={stage.stageId}
                                className={`border-b border-gray-200 ${i % 2 === 0 ? "bg-white" : "bg-gray-50"}`}>
                                <td className="py-2.5 px-3 text-sm font-medium text-zinc-950 sticky left-0 z-10 bg-inherit">
                                    {stage.stageName}
                                </td>
                                <td className="py-2.5 px-3 text-sm text-center tabular-nums font-medium text-zinc-700">
                                    {teamRate}%
                                </td>
                                {sortedBreakdowns.map(ub => {
                                    const rateMap = userRateMaps.get(ub.userId);
                                    const rate = rateMap?.get(stage.stageId) ?? 0;
                                    const cellColor = matrixCellColor(rate, teamRate);
                                    return (
                                        <td
                                            key={ub.userId}
                                            className="py-2.5 px-3 text-sm text-center tabular-nums"
                                            style={{
                                                backgroundColor: cellColor,
                                            }}>
                                            {rate}%
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

function matrixCellColor(rate: number, teamRate: number): string {
    if (rate >= teamRate) return "rgba(34, 197, 94, 0.15)";
    if (rate < teamRate - 10) return "rgba(239, 68, 68, 0.15)";
    return "transparent";
}

const InsightStrip: React.FC<{ insights: string[] }> = ({ insights }) => {
    return (
        <div className="rounded-lg px-4 py-3" style={{ backgroundColor: "#1A1A18" }}>
            <h3 className="text-xs font-medium text-gray-400 mb-2">Insights</h3>
            <ul className="space-y-1">
                {insights.map((insight, i) => (
                    <li key={i} className="text-sm text-gray-200">
                        {insight}
                    </li>
                ))}
            </ul>
        </div>
    );
};
